import {
  createDisabledAwareCurrencyFormatter,
  createDisabledAwareNumberFormatter,
  createDisabledAwarePercentageFormatter,
} from "./formatters";
import {
  MAX_DECIMAL_NUMBER,
  MAX_DOLLAR_AMOUNT,
  MAX_GENERIC_NUMBER,
  MAX_SALARY_MULTIPLIER,
} from "./constants";

import type { CellSpec } from "./types";

export const dollarAmount: CellSpec = {
  cellType: "number",
  dataType: "number",
  fieldName: "amount",
  cellRenderer: createDisabledAwareCurrencyFormatter({
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }),
  cellEditor: "agNumberCellEditor",
  cellEditorParams: {
    min: 0,
    max: MAX_DOLLAR_AMOUNT,
    precision: 0,
  },
  description: "Dollar amount field with currency formatting",
  validation: {
    required: false,
    minimum: 0,
    maximum: MAX_DOLLAR_AMOUNT,
  },
};

export const dollarAmountWithDecimals: CellSpec = {
  cellType: "number",
  dataType: "number",
  fieldName: "amount",
  cellRenderer: createDisabledAwareCurrencyFormatter({
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
  cellEditor: "agNumberCellEditor",
  cellEditorParams: {
    min: 0,
    max: MAX_DOLLAR_AMOUNT,
    precision: 2,
  },
  valueParser: (params) => {
    const value =
      typeof params.newValue === "string"
        ? parseFloat(params.newValue)
        : params.newValue;

    if (isNaN(value) || value === null || value === undefined) {
      return 0;
    }

    const hasDecimals = value % 1 !== 0;

    if (hasDecimals) {
      return Math.round(value * 100) / 100;
    }

    return value;
  },
  description:
    "Dollar amount field with currency formatting and 2 decimal places",
  validation: {
    required: false,
    minimum: 0,
    maximum: MAX_DOLLAR_AMOUNT,
  },
};

export const decimalNumber: CellSpec = {
  cellType: "number",
  dataType: "number",
  cellRenderer: createDisabledAwareNumberFormatter({
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }),
  cellEditor: "agNumberCellEditor",
  cellEditorParams: {
    min: 0,
    max: MAX_DECIMAL_NUMBER,
    precision: 2,
  },
  description: "Decimal number field (e.g., salary multipliers)",
  validation: {
    required: false,
    minimum: 0,
    maximum: MAX_DECIMAL_NUMBER,
  },
};

export const numberField: CellSpec = {
  cellType: "number",
  dataType: "integer",
  cellRenderer: createDisabledAwareNumberFormatter({
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }),
  cellEditor: "agNumberCellEditor",
  cellEditorParams: {
    min: 0,
    max: MAX_GENERIC_NUMBER,
    precision: 0,
  },
  description: "Generic number field (commonly used for days, months, counts)",
  validation: {
    required: false,
    minimum: 0,
    maximum: MAX_GENERIC_NUMBER,
  },
};

export const percentageAmount: CellSpec = {
  cellType: "number",
  dataType: "number",
  cellRenderer: createDisabledAwarePercentageFormatter(),
  cellEditor: "agNumberCellEditor",
  cellEditorParams: {
    min: 0,
    max: 100,
  },
  description: "Percentage field (0-100) with % formatting",
  validation: {
    required: false,
    minimum: 0,
    maximum: 100,
  },
};

export const wholePercentageAmount: CellSpec = {
  cellType: "number",
  dataType: "integer",
  cellRenderer: createDisabledAwarePercentageFormatter(),
  cellEditor: "agNumberCellEditor",
  cellEditorParams: {
    min: 0,
    max: 100,
    precision: 0,
  },
  description: "Whole-number percentage field (0-100) with % formatting",
  validation: {
    required: false,
    minimum: 0,
    maximum: 100,
  },
};

export const percentageAmountHighRange: CellSpec = {
  cellType: "number",
  dataType: "number",
  cellRenderer: createDisabledAwarePercentageFormatter(),
  cellEditor: "agNumberCellEditor",
  cellEditorParams: {
    min: 0,
    max: MAX_GENERIC_NUMBER,
  },
  description: "Percentage field (0-10,000) with % formatting",
  validation: {
    required: false,
    minimum: 0,
    maximum: MAX_GENERIC_NUMBER,
  },
};

export const salaryMultiplier: CellSpec = {
  cellType: "number",
  dataType: "number",
  cellRenderer: createDisabledAwareNumberFormatter({
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }),
  cellEditor: "agNumberCellEditor",
  cellEditorParams: {
    min: 0,
    max: MAX_SALARY_MULTIPLIER,
    precision: 2,
  },
  suffix: "times salary",
  description: 'Salary multiplier displayed as "X times salary"',
  validation: {
    required: false,
    minimum: 0,
    maximum: MAX_SALARY_MULTIPLIER,
  },
};
